import { isStage, key, origin, script } from './snippet.js';
import { nudgeEventKeyPrefix, trustEventKeyPrefix } from './constants.js';
import { onPathChange } from './page.js';

/* ------------------------------------------------------------------------------------------------
 * Analytics (best-effort, consent-gated). Everything is wrapped defensively: a failure inside
 * analytics must never affect the widget or the host page. Without consent no identifiers exist and
 * no browser storage is touched — only anonymous countable events (pageview, impression, open) are
 * beaconed; the server aggregates them into daily counters. With consent (host page sets
 * data-leadwin-consent="granted" or calls LeadWin('consent', 'granted')) a visitor id
 * (localStorage, ~13 months) and a session id (localStorage, 30-min sliding window) are kept and
 * dwell/scroll/heartbeat tracking runs. Transport is sendBeacon/keepalive-fetch with a text/plain
 * body (CORS simple request); the response is read only where a panel exists to open (see flush).
 * pv_close events are additionally stashed in localStorage and redelivered from the next document
 * (rt mark, server-side dedupe) — they are sent exactly once during page teardown, where a rate
 * limit or network loss would otherwise silently erase the page from the visit's journey.
 * ---------------------------------------------------------------------------------------------- */
export const analytics = (function () {
    /* ?stage=1 on the simulated visit, the same marker the config request carries. The endpoint drops a
       beacon whose session belongs to the widget's own owner, because a provider reading their own pages is
       not one of their visitors — and the simulation is the one page where the owner is deliberately playing
       one. Without this the stage would run and the live list would stay empty, which is the whole thing it
       was built to disprove. */
    const ingestUrl = origin + '/widget/' + encodeURIComponent(key) + '/analytics/events'
        + (isStage ? '?stage=1' : '');
    const defaultHeartbeatMs = 20000;

    /* The server may ask for a faster pulse while the agency has this widget's live view open: a
       contractor-initiated conversation only reaches a closed panel with the next beacon response, so
       the pulse is what the visitor waits. Clamped rather than trusted outright — the interval runs on
       the customer's page, and nothing there should be able to talk this loader into hammering. */
    const minHeartbeatMs = 2000;
    const maxHeartbeatMs = 300000;
    let heartbeatMs = defaultHeartbeatMs;
    const flushDelayMs = 1500;
    const sessionIdleMs = 30 * 60 * 1000;
    const visitorTtlMs = 395 * 24 * 60 * 60 * 1000; // ~13 months
    const vidKey = 'feedbax_analytics:vid:' + key;
    const sidKey = 'feedbax_analytics:sid:' + key;
    const lastSeenKey = 'feedbax_analytics:last:' + key;

    /* Unconfirmed pv_close events waiting for redelivery (consented mode only — pv_close does not
       exist without consent). A close is usually sent while the page is being torn down, where the
       response is unreadable and a rate-limited or dropped request dies silently — and unlike a
       heartbeat, a close is sent exactly once, so losing it loses the page from the visit's journey
       for good. Stashed here at close time, redelivered from the NEXT document with an rt mark the
       server deduplicates on, and removed once any live-page flush containing it answers 200. */
    /* Per-tab key for live presence, in sessionStorage BECAUSE it is per tab: it survives navigations
       inside the tab and is never shared with a sibling — which is what lets the dashboard tell a
       visitor's open tabs apart instead of showing one page flip-flopping. Consented mode only. */
    const tabStorageKey = 'feedbax_analytics:tab:' + key;
    const pendingCloseKey = 'feedbax_analytics:pvq:' + key;
    const pendingCloseMaxEntries = 8;
    /* Well under the server's ±24h timestamp clamp: an older redelivery would be re-stamped to "now"
       and place the pageview at the wrong time, which is worse than the gap it tries to fill. */
    const pendingCloseMaxAgeMs = 6 * 60 * 60 * 1000;

    let consent = false;
    let visitorId = null;
    let sessionId = null;
    let tabId = null;
    let queue = [];
    let flushTimer = null;
    let openRequestHandler = null;
    let heartbeatTimer = null;
    let dwellAccumulatedMs = 0;
    let dwellVisibleSince = null;
    let maxScrollPercent = 0;
    let currentPath = null;
    let impressionSent = false;
    let openSent = false;
    /* One impression per launcher surface KEY and pageview, and one click likewise: the trust corner
     * deals a new card every few seconds, and counting each deal would both blow past the batch cap and
     * make a click rate that cannot be read beside the campaigns'. Counted per key, so a corner that
     * alternates quotes and seals reports one of each. The click side is deduplicated for the same
     * reason: a rate over the pageviews a form was shown in stays a rate, and repeated taps on a card
     * that is still standing there would push it past 100%. */
    let surfaceImpressionsSent = new Set();
    let surfaceClicksSent = new Set();
    let started = false;
    let interactionSeen = false;

    function newId() {
        try {
            return crypto.randomUUID();
        } catch (e) {
            return null; // Ancient browser/insecure context — analytics degrades to consentless.
        }
    }

    function readParam(name) {
        try {
            return new URLSearchParams(location.search).get(name) || undefined;
        } catch (e) {
            return undefined;
        }
    }

    function buildContext() {
        const utm = {
            s: readParam('utm_source'), m: readParam('utm_medium'), c: readParam('utm_campaign'),
            t: readParam('utm_term'), n: readParam('utm_content')
        };
        return {
            path: currentPath,
            ref: document.referrer || undefined,
            utm: (utm.s || utm.m || utm.c || utm.t || utm.n) ? utm : undefined,
            gclid: readParam('gclid'),
            msclkid: readParam('msclkid'),
            vw: window.innerWidth,
            vh: window.innerHeight,
            lang: navigator.language || undefined,
            wd: navigator.webdriver === true || undefined,
            itx: interactionSeen || undefined,
            tb: consent && tabId ? tabId : undefined
        };
    }

    /**
     * First trusted input event (pointer/touch/key/wheel) marks the page as interacted-with — a
     * plausibility signal the server uses against headless bots. Deliberately no 'scroll': pages
     * scroll programmatically (anchor jumps, smooth-scroll libraries) without any user present.
     */
    function hookInteraction() {
        const inputEvents = ['pointerdown', 'mousemove', 'touchstart', 'keydown', 'wheel'];
        function markInteraction() {
            interactionSeen = true;
            for (const name of inputEvents) {
                document.removeEventListener(name, markInteraction, true);
            }
        }
        for (const name of inputEvents) {
            document.addEventListener(name, markInteraction, { capture: true, passive: true });
        }
    }

    /**
     * Visitor id (localStorage, expiry-refreshed) + session id (30-min sliding window across tabs).
     * The session id lives in localStorage, not sessionStorage, because a session is the visitor's
     * visit, not one tab of it: sessionStorage is per tab, so a second tab would open a second session
     * with its own key — and since the live view is keyed by that key, the dashboard would list one
     * person twice. The window slides on the shared lastSeen stamp, so any tab keeps the session alive.
     */
    function ensureIds() {
        try {
            const now = Date.now();

            let visitor = null;
            try {
                visitor = JSON.parse(localStorage.getItem(vidKey) || 'null');
            } catch (e) { /* corrupt entry — regenerate below */ }
            if (!visitor || !visitor.id || !(visitor.exp > now)) {
                visitor = { id: newId(), exp: 0 };
            }
            if (!visitor.id) {
                return false;
            }
            visitor.exp = now + visitorTtlMs;
            localStorage.setItem(vidKey, JSON.stringify(visitor));
            visitorId = visitor.id;

            const lastSeen = parseInt(localStorage.getItem(lastSeenKey) || '0', 10);
            sessionId = localStorage.getItem(sidKey);
            if (!sessionId || !(now - lastSeen < sessionIdleMs)) {
                sessionId = newId();
                if (!sessionId) {
                    return false;
                }
                localStorage.setItem(sidKey, sessionId);
            }
            localStorage.setItem(lastSeenKey, String(now));
            ensureTabId();
            return true;
        } catch (e) {
            return false; // Storage blocked — degrade to consentless counting.
        }
    }

    /**
     * Best effort, never fatal: a session without a tab key still tracks — the presence view then files
     * this tab under the shared legacy slot, exactly as an old cached loader would.
     */
    function ensureTabId() {
        try {
            tabId = sessionStorage.getItem(tabStorageKey);
            if (!tabId) {
                tabId = newId();
                if (tabId) {
                    sessionStorage.setItem(tabStorageKey, tabId);
                }
            }
        } catch (e) {
            tabId = tabId || newId(); // Storage blocked — a per-document key still tells tabs apart.
        }
    }

    function currentDwellMs() {
        const running = dwellVisibleSince !== null ? Date.now() - dwellVisibleSince : 0;
        return Math.max(0, Math.round(dwellAccumulatedMs + running));
    }

    function trackScroll() {
        try {
            const doc = document.documentElement;
            const total = Math.max(doc.scrollHeight, 1);
            const seen = Math.min(total, (window.scrollY || doc.scrollTop || 0) + window.innerHeight);
            maxScrollPercent = Math.max(maxScrollPercent, Math.round(seen / total * 100));
        } catch (e) { /* never break the host page */ }
    }

    function enqueue(type, extra) {
        const ev = { t: type, ts: Date.now() };
        if (extra) {
            if (extra.path) { ev.path = extra.path; }
            if (extra.dwellMs !== undefined) { ev.dwellMs = extra.dwellMs; }
            if (extra.scroll !== undefined) { ev.scroll = extra.scroll; }
            if (extra.key) { ev.k = extra.key; }
        }
        queue.push(ev);
        scheduleFlush();
        return ev;
    }

    /** @returns {Array<{path: string, ts: number, dwellMs: number, scroll: number, sid: string}>} */
    function readPendingCloses() {
        try {
            const list = JSON.parse(localStorage.getItem(pendingCloseKey) || '[]');
            return Array.isArray(list) ? list : [];
        } catch (e) {
            return []; // Corrupt or blocked storage — nothing to redeliver.
        }
    }

    /** @param {Array<{path: string, ts: number, dwellMs: number, scroll: number, sid: string}>} list - Entries to keep */
    function writePendingCloses(list) {
        try {
            if (list.length === 0) {
                localStorage.removeItem(pendingCloseKey);
            } else {
                localStorage.setItem(pendingCloseKey, JSON.stringify(list.slice(-pendingCloseMaxEntries)));
            }
        } catch (e) { /* storage may vanish mid-session — redelivery is best effort */ }
    }

    /**
     * Remembers a close for redelivery until a readable 200 confirms it left. Two tabs may interleave
     * their read-modify-write here; the worst outcomes are a duplicate redelivery (the server
     * deduplicates) or one lost entry (no worse than having no stash at all).
     * @param {{path?: string, ts: number, dwellMs?: number, scroll?: number}} ev - The queued pv_close
     */
    function stashPendingClose(ev) {
        const list = readPendingCloses();
        list.push({
            path: ev.path || '/', ts: ev.ts, dwellMs: ev.dwellMs || 0, scroll: ev.scroll || 0,
            sid: sessionId
        });
        writePendingCloses(list);
    }

    /**
     * Drops every stashed close a delivered batch contained — the 200 is the proof of delivery the
     * teardown send never gets to see.
     * @param {Array<{t: string, ts: number, path?: string}>} events - The batch that answered 200
     */
    function confirmPendingCloses(events) {
        const delivered = events.filter(function (ev) { return ev.t === 'pv_close'; });
        if (delivered.length === 0) {
            return;
        }
        const remaining = readPendingCloses().filter(function (pending) {
            return !delivered.some(function (ev) { return ev.ts === pending.ts && ev.path === pending.path; });
        });
        writePendingCloses(remaining);
    }

    /**
     * Re-queues the closes of earlier documents that were never confirmed, marked rt so the server can
     * drop the ones whose original did arrive. Called from a fresh document only: the entries stay
     * stashed until a readable response confirms this delivery, so a redelivery may itself be retried.
     * Only closes of the RUNNING session travel — the batch carries the current session key, and a
     * close whose session has rotated away would land as a page of a visit it never belonged to. The
     * rotated leftovers are dropped for good in the same pass.
     */
    function resendPendingCloses() {
        const now = Date.now();
        const list = readPendingCloses().filter(function (pending) {
            return pending && typeof pending.path === 'string' && typeof pending.ts === 'number'
                && pending.sid === sessionId
                && now - pending.ts < pendingCloseMaxAgeMs;
        });
        writePendingCloses(list);
        for (const pending of list) {
            queue.push({
                t: 'pv_close', ts: pending.ts, path: pending.path,
                dwellMs: pending.dwellMs, scroll: pending.scroll, rt: true
            });
        }
        if (list.length > 0) {
            scheduleFlush();
        }
    }

    function scheduleFlush() {
        if (flushTimer === null) {
            flushTimer = setTimeout(function () {
                flushTimer = null;
                flush();
            }, flushDelayMs);
        }
    }

    function flush() {
        if (queue.length === 0) {
            return;
        }
        const events = queue.splice(0, 20); // server-side batch cap
        const payload = {
            v: 1,
            consent: consent && sessionId !== null,
            vk: consent ? visitorId : undefined,
            sk: consent ? sessionId : undefined,
            ctx: buildContext(),
            events: events
        };
        try {
            const body = JSON.stringify(payload);
            let sent = false;
            // With a registered open handler the beacons travel as keepalive fetch instead of
            // sendBeacon: only fetch can read the response, and the server answers a heartbeat with
            // an open flag when a contractor started a conversation for this session.
            if (openRequestHandler !== null && typeof fetch === 'function') {
                sent = true;
                fetch(ingestUrl, {
                    method: 'POST',
                    body: body,
                    keepalive: true,
                    headers: { 'Content-Type': 'text/plain' }
                }).then(function (resp) {
                    if (!resp || resp.status !== 200) {
                        return null;
                    }
                    confirmPendingCloses(events);
                    return resp.json();
                }).then(function (data) {
                    if (!data) {
                        return;
                    }
                    if (data.open === true && openRequestHandler !== null) {
                        openRequestHandler();
                    }
                    applyHeartbeatHint(data.hb);
                }).catch(function () { /* fire-and-forget */ });
            }
            if (!sent && navigator.sendBeacon) {
                sent = navigator.sendBeacon(ingestUrl, new Blob([body], { type: 'text/plain' }));
            }
            if (!sent && typeof fetch === 'function') {
                fetch(ingestUrl, {
                    method: 'POST',
                    body: body,
                    keepalive: true,
                    headers: { 'Content-Type': 'text/plain' }
                }).then(function (resp) {
                    if (resp && resp.status === 200) {
                        confirmPendingCloses(events);
                    }
                }).catch(function () { /* fire-and-forget */ });
            }
            if (consent) {
                try {
                    localStorage.setItem(lastSeenKey, String(Date.now())); // sliding session window
                } catch (e) { /* storage may vanish mid-session */ }
            }
        } catch (e) { /* analytics must never throw into the host page */ }
        if (queue.length > 0) {
            scheduleFlush(); // more than one batch worth queued (rare)
        }
    }

    function startHeartbeat() {
        if (heartbeatTimer !== null) {
            return;
        }
        heartbeatTimer = setInterval(function () {
            if (!document.hidden) {
                trackScroll();
                enqueue('hb', { dwellMs: currentDwellMs(), scroll: maxScrollPercent });
                flushNow();
            }
        }, heartbeatMs);
    }

    /**
     * Adopts the pulse the server asked for. Re-arming means the change takes effect after one more
     * interval at the old rate; that is deliberate — restarting the timer on every response would let a
     * page whose beacons keep landing just before the tick postpone its own heartbeat indefinitely.
     * @param {unknown} seconds - The `hb` field of a beacon response, in seconds.
     */
    function applyHeartbeatHint(seconds) {
        if (typeof seconds !== 'number' || !isFinite(seconds)) {
            return;
        }
        const wanted = Math.min(maxHeartbeatMs, Math.max(minHeartbeatMs, Math.round(seconds * 1000)));
        if (wanted === heartbeatMs) {
            return;
        }
        heartbeatMs = wanted;
        if (heartbeatTimer !== null) {
            clearInterval(heartbeatTimer);
            heartbeatTimer = null;
            startHeartbeat();
        }
    }

    function flushNow() {
        if (flushTimer !== null) {
            clearTimeout(flushTimer);
            flushTimer = null;
        }
        flush();
    }

    function openPageview() {
        currentPath = location.pathname || '/';
        dwellAccumulatedMs = 0;
        dwellVisibleSince = document.hidden ? null : Date.now();
        maxScrollPercent = 0;
        impressionSent = false;
        surfaceImpressionsSent = new Set();
        surfaceClicksSent = new Set();
        trackScroll();
        enqueue('pv_open', { path: currentPath });
        if (document.getElementById('leadwin-agent-button')) {
            onImpression(); // SPA route change: the launcher is already on the page.
        }
    }

    function closePageview() {
        if (currentPath === null) {
            return;
        }
        if (consent) {
            // pv_close is a consented-only signal (dwell/scroll); anonymous counting has no use for it.
            // Stashed for redelivery too: this send usually races the page teardown, and its loss is
            // the one loss the journey cannot repair from later beacons.
            stashPendingClose(
                enqueue('pv_close', { path: currentPath, dwellMs: currentDwellMs(), scroll: maxScrollPercent }));
        }
        flushNow();
    }

    /** Guarded SPA support: history API navigations close the old pageview and open a new one. */
    function hookSpaNavigation() {
        onPathChange(function () {
            // Measured against the PATHNAME, not against the key the hook hands out: a pageview is a page
            // here, and it has been ever since this existed — the traffic numbers in the manage panel are
            // per path, so counting a section switch as a second view would silently restate them. What
            // the argument would buy is a close and an open on every screen change, and on a site whose
            // provider named a parameter, one on every tracking link too.
            if ((location.pathname || '/') !== currentPath) {
                closePageview();
                openPageview();
            }
        });
    }

    function start() {
        if (started) {
            return;
        }
        started = true;

        if (consent && !ensureIds()) {
            consent = false; // no usable ids (storage blocked) → anonymous counting only
        }

        hookInteraction();
        document.addEventListener('scroll', trackScroll, { passive: true });
        document.addEventListener('visibilitychange', function () {
            if (document.hidden) {
                if (dwellVisibleSince !== null) {
                    dwellAccumulatedMs += Date.now() - dwellVisibleSince;
                    dwellVisibleSince = null;
                }
                if (consent) {
                    trackScroll();
                    enqueue('hb', { dwellMs: currentDwellMs(), scroll: maxScrollPercent });
                }
                flushNow(); // hidden tabs may never come back — get the queue out now
            } else {
                dwellVisibleSince = Date.now();
            }
        });
        window.addEventListener('pagehide', closePageview);

        openPageview();
        hookSpaNavigation();
        if (consent) {
            startHeartbeat();
            // A fresh document is the first safe moment to redeliver what the last one could not
            // confirm — its own closes only ever stash at teardown, so nothing here re-queues itself.
            resendPendingCloses();
        }
    }

    function onImpression() {
        if (!impressionSent) {
            impressionSent = true;
            enqueue('widget_impression', { path: currentPath });
        }
    }

    function onWidgetOpened() {
        if (!openSent) {
            openSent = true;
            enqueue('widget_open', { path: currentPath });
            flushNow();
        }
    }

    /* Campaign counters. Both are countable events (aggregated daily, no identity), so they ride the
     * batch with or without consent — the campaign key is the campaign's own id, not the visitor's. */
    function onAnnouncementImpression(announcementId) {
        enqueue('ann_impression', { path: currentPath, key: announcementId });
    }

    function onAnnouncementClick(announcementId) {
        enqueue('ann_click', { path: currentPath, key: announcementId });
        flushNow(); // A click often navigates away — get the event out while the page still stands.
    }

    /* Launcher surfaces. Countable like the campaigns above — the key is a card form of the provider's
     * or the id of a bubble they wrote, never anything about the visitor — so both ride the batch with
     * or without consent. */

    /**
     * One impression per key and pageview; every later deal of the same key is dropped here.
     * @param {string} type - Beacon discriminator
     * @param {string} key - Prefixed wire key
     */
    function trackSurfaceImpression(type, key) {
        if (!key || surfaceImpressionsSent.has(key)) {
            return;
        }
        surfaceImpressionsSent.add(key);
        enqueue(type, { path: currentPath, key: key });
    }

    /**
     * One click per key and pageview, flushed at once — a tapped surface opens the chat, and the panel
     * going up is the moment a queued beacon is most likely to be lost.
     * @param {string} type - Beacon discriminator
     * @param {string} key - Prefixed wire key
     */
    function trackSurfaceClick(type, key) {
        if (!key || surfaceClicksSent.has(key)) {
            return;
        }
        surfaceClicksSent.add(key);
        enqueue(type, { path: currentPath, key: key });
        flushNow();
    }

    function onTrustImpression(kind) {
        trackSurfaceImpression('trust_impression', kind ? trustEventKeyPrefix + kind : '');
    }

    function onTrustClick(kind) {
        trackSurfaceClick('trust_click', kind ? trustEventKeyPrefix + kind : '');
    }

    function onNudgeImpression(nudgeId) {
        trackSurfaceImpression('nudge_impression', nudgeId ? nudgeEventKeyPrefix + nudgeId : '');
    }

    function onNudgeClick(nudgeId) {
        trackSurfaceClick('nudge_click', nudgeId ? nudgeEventKeyPrefix + nudgeId : '');
    }

    /** Query-string addition carrying the session id into the widget iframe (funnel correlation). */
    function frameQuery(baseUrl) {
        if (!consent || !sessionId) {
            return '';
        }
        return (baseUrl.indexOf('?') >= 0 ? '&' : '?') + 'sid=' + encodeURIComponent(sessionId);
    }

    /** Host-page consent API: attribute for static setups, the consent command for CMP callbacks. */
    function setConsent(state) {
        const granted = state === 'granted';
        if (granted === consent) {
            return;
        }
        consent = granted;
        if (!started) {
            return; // start() will honor the flag
        }
        if (granted) {
            if (ensureIds()) {
                openPageview(); // the consented session starts from here
                startHeartbeat();
                resendPendingCloses(); // closes stashed on an earlier consented page of this visit
            } else {
                consent = false;
            }
        } else {
            sessionId = null;
            visitorId = null;
            tabId = null;
            try {
                localStorage.removeItem(vidKey);
                localStorage.removeItem(lastSeenKey);
                localStorage.removeItem(sidKey);
                localStorage.removeItem(pendingCloseKey); // withdrawn consent redelivers nothing
                sessionStorage.removeItem(tabStorageKey);
            } catch (e) { /* storage may be blocked */ }
        }
    }

    // Guarded like every other read of the tag: this module's body runs on declaration, which is
    // before the bootstrap gets to ask whether there is a usable snippet at all.
    consent = script ? script.getAttribute('data-leadwin-consent') === 'granted' : false;

    return {
        start: start,
        /** LeadWin('consent', …): the host page's cookie banner lifts or holds the session. */
        setConsent: setConsent,
        onImpression: onImpression,
        onWidgetOpened: onWidgetOpened,
        onAnnouncementImpression: onAnnouncementImpression,
        onAnnouncementClick: onAnnouncementClick,
        onTrustImpression: onTrustImpression,
        onTrustClick: onTrustClick,
        onNudgeImpression: onNudgeImpression,
        onNudgeClick: onNudgeClick,
        frameQuery: frameQuery,
        /** The widget UI registers how to open its panel when a beacon response asks for it. */
        onOpenRequested: function (handler) {
            openRequestHandler = typeof handler === 'function' ? handler : null;
        }
    };
})();
